import { useState, useEffect, useLayoutEffect, useRef } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { Home, Building2, LayoutGrid, TrendingUp, FolderGit2, MessageCircle, FileText } from 'lucide-react'
import useActiveSection from '../../hooks/useActiveSection'
import { scrollToSection } from '../../utils/helpers'
import { NAVIGATION_ITEMS } from '../../utils/constants'
import { SPRING } from '../../utils/motion'
import InstallPrompt from '../ui/InstallPrompt'
import Wordmark from '../ui/Wordmark'

const SECTION_IDS = NAVIGATION_ITEMS.map((item) => item.href.replace('#', ''))

const ICONS = {
  home: Home,
  about: Building2,
  services: LayoutGrid,
  impact: TrendingUp,
  portfolio: FolderGit2,
  contact: MessageCircle,
}

// The indicator's two edges ride slightly different springs: the edge heading
// towards the new destination leads by a beat. Both are critically damped.
const LEAD = { type: 'spring', stiffness: 700, damping: 56, mass: 1 }
const TRAIL = { type: 'spring', stiffness: 420, damping: 44, mass: 1 }

/* Desktop: a Material 3 top app bar in an industrial cut. At the top of the
   page it sits flat on the canvas; once you scroll it contracts into a
   floating tonal toolbar with tight corners. Destinations carry icon + label
   with state layers, the active one sits on a secondary-container indicator,
   and the CTA is a square-shouldered extended FAB. */
export default function Navbar() {
  const [scrolled, setScrolled] = useState(false)
  const activeSection = useActiveSection(SECTION_IDS)
  const reduce = useReducedMotion()

  const navRef = useRef(null)
  const itemRefs = useRef({})
  const [indicator, setIndicator] = useState(null)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // offsetLeft/offsetWidth ignore transforms, so the measurement is stable
  // while the bar itself is transitioning.
  useLayoutEffect(() => {
    const nav = navRef.current
    if (!nav) return
    const measure = () => {
      const el = itemRefs.current[activeSection]
      if (!el) return
      const left = el.offsetLeft
      const right = nav.offsetWidth - (left + el.offsetWidth)
      setIndicator((prev) => ({
        left,
        right,
        towardsRight: prev ? left > prev.left : true,
      }))
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(nav)
    return () => ro.disconnect()
  }, [activeSection])

  const go = (e, href) => {
    e.preventDefault()
    scrollToSection(href.replace('#', ''))
  }

  const edge = (isLeading) => (reduce ? { duration: 0 } : isLeading ? LEAD : TRAIL)

  return (
    <header className="fixed inset-x-0 top-0 z-50 pointer-events-none">
      <div
        className={`pointer-events-auto mx-auto flex items-center justify-between transition-all duration-300 ease-emphasized motion-reduce:transition-none ${
          scrolled
            ? 'mt-3 h-16 max-w-6xl rounded-full border border-outline-variant bg-sc-lowest/80 px-2 pl-6 shadow-[inset_0_1px_0_rgb(var(--highlight)),var(--elev-3)] backdrop-blur-xl backdrop-saturate-150 md:mx-6 lg:mx-auto'
            : 'mt-1 h-20 max-w-6xl rounded-none border border-transparent bg-transparent px-6 lg:px-10 xl:max-w-7xl 2xl:max-w-[90rem] 2xl:px-16'
        }`}
      >
        <a href="#home" onClick={(e) => go(e, '#home')} className="tap-scale shrink-0">
          <Wordmark className="h-7 md:h-8" />
        </a>

        <nav ref={navRef} className="relative hidden md:flex items-center gap-0.5" aria-label="Primary">
          {indicator && (
            <motion.span
              aria-hidden="true"
              className="absolute inset-y-0 rounded-full bg-secondary-container shadow-[inset_0_1px_0_rgb(var(--highlight))]"
              initial={false}
              animate={{ left: indicator.left, right: indicator.right }}
              transition={{
                left: edge(!indicator.towardsRight),
                right: edge(indicator.towardsRight),
              }}
            />
          )}
          {NAVIGATION_ITEMS.map((item) => {
            const id = item.href.replace('#', '')
            const active = activeSection === id
            const Icon = ICONS[id]
            return (
              <a
                key={item.label}
                ref={(el) => (itemRefs.current[id] = el)}
                href={item.href}
                onClick={(e) => go(e, item.href)}
                aria-current={active ? 'location' : undefined}
                className={`state-layer relative inline-flex h-10 items-center whitespace-nowrap gap-2 rounded-full px-3 text-[14px] font-medium transition-colors duration-200 lg:px-3.5 ${
                  active ? 'text-secondary-on-container' : 'text-ink-3 hover:text-ink'
                }`}
              >
                {Icon && (
                  <span className="relative hidden lg:inline-flex">
                    <Icon className="h-[17px] w-[17px]" strokeWidth={active ? 2.25 : 1.75} />
                  </span>
                )}
                <span className={`relative ${active ? 'font-semibold' : ''}`}>{item.label}</span>
              </a>
            )
          })}
        </nav>

        <div className="flex items-center gap-3">
          <motion.a
            href="#contact"
            onClick={(e) => go(e, '#contact')}
            whileTap={reduce ? undefined : { scale: 0.98 }}
            transition={SPRING.snappy}
            className="btn-lit state-layer hidden h-11 shrink-0 select-none items-center gap-2 whitespace-nowrap rounded-full px-5 text-[14px] font-semibold text-primary-on md:inline-flex"
          >
            <FileText className="h-[17px] w-[17px]" strokeWidth={2} />
            Request a quote
          </motion.a>
          <div className="md:hidden">
            <InstallPrompt />
          </div>
        </div>
      </div>
    </header>
  )
}
